'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';

const navGroups = [
  {
    label: 'Discover',
    items: [
      { href: '/', label: 'Discovery Feed', icon: 'SparklesIcon', badge: null },
      { href: '/find-a-creator', label: 'Find a Creator', icon: 'MagnifyingGlassIcon', badge: null },
    ],
  },
  {
    label: 'ISX Platform',
    items: [
      { href: '/isx-scoring', label: 'ISX Scoring', icon: 'ChartBarIcon', badge: null },
      { href: '/isx-observatory', label: 'Observatory', icon: 'GlobeAltIcon', badge: null },
      { href: '/verified-deals', label: 'Deal Registry', icon: 'CheckBadgeIcon', badge: null },
      { href: '/score-dispute', label: 'Dispute & RGPD', icon: 'FlagIcon', badge: null },
    ],
  },
  {
    label: 'Creator',
    items: [
      { href: '/creator-professional-page', label: 'Mon Profil ISX', icon: 'UserCircleIcon', badge: null },
      { href: '/professional-passport', label: 'Passeport Pro', icon: 'DocumentTextIcon', badge: null },
      { href: '/professional-reputation', label: 'Réputation (PRS)', icon: 'StarIcon', badge: null },
      { href: '/creator-revenue', label: 'Revenus & Docs', icon: 'BanknotesIcon', badge: null },
    ],
  },
  {
    label: 'Campaigns',
    items: [
      { href: '/campaign-builder', label: 'Campaign Builder', icon: 'RocketLaunchIcon', badge: '3' },
      { href: '/influence-campaigns', label: 'Campagnes Influence', icon: 'MegaphoneIcon', badge: null },
      { href: '/brand-portal', label: 'Brand Portal', icon: 'BuildingOffice2Icon', badge: null },
      { href: '/brand-watchlist', label: 'Watchlist & Portfolios', icon: 'BookmarkIcon', badge: null },
      { href: '/brand-publications', label: 'Publications Marques', icon: 'PencilSquareIcon', badge: null },
    ],
  },
  {
    label: 'Platform',
    items: [
      { href: '/admin-panel', label: 'Admin Panel', icon: 'ShieldCheckIcon', badge: '7' },
    ],
  },
];

interface SidebarProps {
  currentPath: string;
}

export default function Sidebar({ currentPath }: SidebarProps) {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <aside
      className="hidden lg:flex flex-col bg-card border-r border-border transition-all duration-300 ease-in-out flex-shrink-0"
      style={{ width: collapsed ? '60px' : '232px' }}
    >
      {/* Logo */}
      <div className="flex items-center h-14 px-4 border-b border-border flex-shrink-0">
        <div className="flex items-center gap-2.5 min-w-0">
          <AppLogo size={28} />
          {!collapsed && (
            <div className="min-w-0">
              <span className="font-700 text-xs text-foreground tracking-tight block">
                <span className="text-gradient-primary">REVERSAL</span>
              </span>
              <span className="text-[10px] text-muted-foreground tracking-wide block">Creatorindex360 · ISX</span>
            </div>
          )}
        </div>
      </div>

      {/* Nav Groups */}
      <nav className="flex-1 py-3 overflow-y-auto overflow-x-hidden hide-scrollbar">
        {navGroups.map((group) => (
          <div key={`group-${group.label}`} className="mb-4">
            {!collapsed && (
              <p className="px-4 mb-1.5 text-[10px] font-700 tracking-widest uppercase text-muted-foreground/60">
                {group.label}
              </p>
            )}
            <ul className="space-y-0.5 px-2">
              {group.items.map((item) => {
                const isActive = currentPath === item.href;
                return (
                  <li key={`nav-${item.href}`} className="relative">
                    {isActive && <span className="nav-active-bar" />}
                    <Link
                      href={item.href}
                      className={`flex items-center gap-2.5 px-3 py-2 rounded-md text-sm font-500 transition-all duration-150 group relative ${
                        isActive
                          ? 'bg-primary/10 text-primary' :'text-muted-foreground hover:bg-muted hover:text-foreground'
                      }`}
                      title={collapsed ? item.label : undefined}
                    >
                      <Icon
                        name={item.icon as Parameters<typeof Icon>[0]['name']}
                        size={16}
                        className={isActive ? 'text-primary' : 'text-muted-foreground group-hover:text-foreground'}
                      />
                      {!collapsed && (
                        <span className="flex-1 truncate text-[13px]">{item.label}</span>
                      )}
                      {!collapsed && item.badge && (
                        <span className="flex-shrink-0 bg-primary/15 text-primary text-[10px] font-700 px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                          {item.badge}
                        </span>
                      )}
                      {collapsed && item.badge && (
                        <span className="absolute top-1 right-1 w-1.5 h-1.5 bg-primary rounded-full" />
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      {/* User + Collapse */}
      <div className="border-t border-border p-2 space-y-1">
        {!collapsed && (
          <div className="flex items-center gap-2.5 px-2 py-2 rounded-md hover:bg-muted transition-colors cursor-pointer">
            <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 border border-primary/30">
              <span className="text-[10px] font-700 text-primary">SK</span>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[12px] font-600 text-foreground truncate">Soudani Khedidja</p>
              <p className="text-[10px] text-muted-foreground truncate">General Manager</p>
            </div>
            <Icon name="ChevronUpDownIcon" size={12} className="text-muted-foreground flex-shrink-0" />
          </div>
        )}
        {collapsed && (
          <div className="flex justify-center py-1">
            <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center border border-primary/30">
              <span className="text-[10px] font-700 text-primary">SK</span>
            </div>
          </div>
        )}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] text-muted-foreground hover:bg-muted hover:text-foreground transition-all duration-150"
        >
          <Icon
            name={collapsed ? 'ChevronRightIcon' : 'ChevronLeftIcon'}
            size={12}
          />
          {!collapsed && <span>Collapse</span>}
        </button>
      </div>
    </aside>
  );
}